/** @file Applies user overrides to a default binding table; an override that would break it is dropped. */
import type { Binding, Platform } from './types';
import { validateTable } from './validateTable';

/** The table to register and the overrides it could not take (reserved, malformed or conflicting). */
export interface OverrideResult {
  table: Binding[];
  dropped: Binding[];
}

const slot = (b: Pick<Binding, 'id' | 'context'>): string => `${b.context}\u0000${b.id}`;

/** The overrides that match a default. */
function known(defaults: readonly Binding[], overrides: readonly Binding[]): Binding[] {
  const slots = new Set(defaults.map(slot));
  return overrides.filter((o) => slots.has(slot(o)));
}

/** Every key change applied at once (the last override per binding wins); `keys: ''` removes the binding. */
function build(defaults: readonly Binding[], active: readonly Binding[]): Binding[] {
  const byslot = new Map(active.map((o) => [slot(o), o]));
  return defaults.flatMap((b) => {
    const o = byslot.get(slot(b));
    if (o === undefined) return [b];
    return o.keys === '' ? [] : [{ ...b, keys: o.keys }];
  });
}

/** Slots of the overrides a validateTable problem is about: the flagged binding and, for a conflict, its partner. */
function involved(table: readonly Binding[], active: readonly Binding[], platform: Platform): Set<string> {
  const changed = new Set(active.filter((o) => o.keys !== '').map(slot));
  const out = new Set<string>();
  for (const error of validateTable([...table], platform)) {
    if (changed.has(slot(error.binding))) out.add(slot(error.binding));
    if (error.code !== 'CONFLICT') continue;
    for (const other of table) {
      if (slot(other) === slot(error.binding) || !changed.has(slot(other))) continue;
      if (validateTable([other, error.binding], platform).some((e) => e.code === 'CONFLICT')) out.add(slot(other));
    }
  }
  return out;
}

/**
 * `{ id, context, keys }` replaces the keys of the default with that id and context; `keys: ''` unbinds it. All key
 * changes apply first, then the whole table is validated and the overrides it flags (including both sides of a clash
 * between two overrides) are dropped and the default stays, so the result does not depend on override order and
 * registerBindings never throws on stored settings. Overrides with no matching default are ignored, not dropped.
 */
export function applyOverrides(defaults: readonly Binding[], overrides: readonly Binding[], platform: Platform): OverrideResult {
  let active = known(defaults, overrides);
  const dropped: Binding[] = [];
  for (;;) {
    const table = build(defaults, active);
    const bad = involved(table, active, platform);
    if (bad.size === 0) return { table, dropped };
    dropped.push(...active.filter((o) => bad.has(slot(o))));
    active = active.filter((o) => !bad.has(slot(o)));
  }
}
